/**
 * Deterministic, client-side synthesizers for SDLC stages that don't yet
 * have a dedicated backend. They generate plausible artefacts (architecture
 * options, documentation, test scaffolds, CI/CD files, deployment manifests) from
 * whatever upstream context is available.
 *
 * Everything here is pure — same input → same output — so the SDLC
 * workspace remains demoable end-to-end without server-side changes. When
 * a real backend is added, swap individual functions out behind a feature
 * flag.
 */

const _safeText = (val, max = 4000) => {
  if (!val) return '';
  if (typeof val === 'string') return val.slice(0, max);
  try { return JSON.stringify(val).slice(0, max); } catch { return ''; }
};

const _extractRequirementsText = (report) => {
  if (!report) return '';
  const parts = [
    report.title,
    report.summary,
    report.idea,
    report.idea_title,
    report.executive_summary,
    report.requirements,
  ];
  if (Array.isArray(report.sections)) {
    for (const s of report.sections) parts.push(s?.title, s?.content);
  }
  return parts.filter(Boolean).map((p) => _safeText(p, 1200)).join('\n');
};

const _slug = (s) =>
  (s || 'project')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'project';

// ----------------------------------------------------------------------------
// Architecture options
// ----------------------------------------------------------------------------

export const synthesizeArchitectureOptions = (requirementsReport) => {
  const ctx = _extractRequirementsText(requirementsReport);
  const dataHeavy = /\b(database|store|persist|record|dataset|catalog)\b/i.test(ctx);
  const realtime = /\b(real[- ]?time|stream|live|websocket|notification)\b/i.test(ctx);
  const aiHeavy = /\b(model|llm|inference|embedding|rag|agent)\b/i.test(ctx);

  return [
    {
      id: 'serverless',
      name: 'Serverless (AWS Lambda + DynamoDB)',
      summary: 'Event-driven serverless backend with managed services. Lowest ops overhead.',
      components: [
        'API Gateway (REST/HTTP)',
        'AWS Lambda (Python 3.13)',
        'Amazon DynamoDB single-table',
        'Amazon S3 (assets / large payloads)',
        'Amazon SQS (async work queue)',
        ...(aiHeavy ? ['Amazon Bedrock (LLM inference)'] : []),
        ...(realtime ? ['API Gateway WebSocket API'] : []),
      ],
      pros: [
        'Pay-per-request, scales to zero',
        'No server patching',
        'Native AWS IAM-based authz',
      ],
      cons: [
        'Cold-start latency for spiky traffic',
        'Per-request cost can grow at very high volume',
      ],
      diagram: 'client → API Gateway → Lambda → DynamoDB / S3 / SQS',
      bestFor: 'MVPs, low/medium traffic, AWS-native teams.',
    },
    {
      id: 'containerized',
      name: 'Containerized (ECS Fargate + Aurora)',
      summary: 'Long-running containers with a managed relational store. Predictable performance.',
      components: [
        'Application Load Balancer',
        'ECS Fargate service (Python/Node container)',
        dataHeavy ? 'Amazon Aurora PostgreSQL Serverless v2' : 'Amazon RDS PostgreSQL',
        'Amazon S3',
        'Amazon ElastiCache (Redis)',
        ...(realtime ? ['ALB + persistent WebSocket workers'] : []),
        ...(aiHeavy ? ['Amazon Bedrock (LLM inference)'] : []),
      ],
      pros: [
        'No cold starts',
        'Long-lived connections (DB pools, websockets)',
        'Easier to port off AWS',
      ],
      cons: [
        'Always-on cost floor',
        'More moving parts (autoscaling, health checks)',
      ],
      diagram: 'client → ALB → ECS Fargate → Aurora / Redis / S3',
      bestFor: 'Steady traffic, latency-sensitive APIs, existing container shops.',
    },
    {
      id: 'modular-monolith',
      name: 'Modular Monolith (Single deployable)',
      summary: 'One service, internal modules. Easiest to build first; split later if needed.',
      components: [
        'Single Python (FastAPI) or Node service',
        dataHeavy ? 'PostgreSQL (managed)' : 'SQLite or PostgreSQL (managed)',
        'In-process job runner (APScheduler / BullMQ)',
        'Local filesystem or S3 for blobs',
        ...(aiHeavy ? ['Direct LLM SDK calls (Bedrock / OpenAI)'] : []),
      ],
      pros: [
        'Simplest to build, test, deploy',
        'Fewer cross-service contracts',
        'One runbook',
      ],
      cons: [
        'Scales vertically first',
        'Harder to isolate failure domains',
      ],
      diagram: 'client → single service → DB',
      bestFor: 'Solo / small teams, early product validation.',
    },
  ];
};

// ----------------------------------------------------------------------------
// Test scaffolding
// ----------------------------------------------------------------------------

const _detectFramework = (files) => {
  const exts = (files || []).map((f) => (f.path || f.name || '').toLowerCase());
  if (exts.some((p) => p.endsWith('.py'))) return 'pytest';
  if (exts.some((p) => p.endsWith('.ts') || p.endsWith('.tsx'))) return 'jest-ts';
  if (exts.some((p) => p.endsWith('.js') || p.endsWith('.jsx'))) return 'jest';
  if (exts.some((p) => p.endsWith('.java'))) return 'junit';
  if (exts.some((p) => p.endsWith('.go'))) return 'go-test';
  return 'pytest';
};

const _testFileForSource = (file, framework) => {
  const path = file.path || file.name || 'module';
  const base = path.replace(/^.*[\\/]/, '').replace(/\.[^.]+$/, '');

  if (framework === 'pytest') {
    return {
      path: `tests/test_${base}.py`,
      content:
        `"""Generated unit tests for ${path}."""\n` +
        `import pytest\n\n` +
        `# TODO: import the actual module under test, e.g.\n` +
        `# from ${base} import main_function\n\n` +
        `def test_${base}_smoke():\n` +
        `    """Smoke test — replace with real assertions."""\n` +
        `    assert True\n\n` +
        `@pytest.mark.parametrize("value,expected", [(1, 1), (2, 2)])\n` +
        `def test_${base}_parametrized(value, expected):\n` +
        `    assert value == expected\n`,
    };
  }
  if (framework === 'jest' || framework === 'jest-ts') {
    const ext = framework === 'jest-ts' ? 'ts' : 'js';
    return {
      path: `tests/${base}.test.${ext}`,
      content:
        `// Generated unit tests for ${path}\n\n` +
        `describe('${base}', () => {\n` +
        `  it('smoke test', () => {\n` +
        `    expect(true).toBe(true);\n` +
        `  });\n\n` +
        `  it.each([[1, 1], [2, 2]])('parametrized %i', (a, b) => {\n` +
        `    expect(a).toBe(b);\n` +
        `  });\n` +
        `});\n`,
    };
  }
  if (framework === 'junit') {
    return {
      path: `src/test/java/${base}Test.java`,
      content:
        `// Generated JUnit 5 tests for ${path}\n` +
        `import org.junit.jupiter.api.Test;\n` +
        `import static org.junit.jupiter.api.Assertions.*;\n\n` +
        `class ${base}Test {\n` +
        `  @Test\n` +
        `  void smoke() {\n` +
        `    assertTrue(true);\n` +
        `  }\n` +
        `}\n`,
    };
  }
  if (framework === 'go-test') {
    return {
      path: `${base}_test.go`,
      content:
        `package main\n\nimport "testing"\n\n` +
        `func Test${base.charAt(0).toUpperCase() + base.slice(1)}_Smoke(t *testing.T) {\n` +
        `  if 1 != 1 { t.Fatal("impossible") }\n` +
        `}\n`,
    };
  }
  return { path: `tests/${base}.test.txt`, content: 'TODO: write tests' };
};

export const synthesizeTests = ({ codeSnapshot, framework }) => {
  const files = codeSnapshot?.files || [];
  const fw = framework || _detectFramework(files);
  const generated = files
    .filter((f) => !/test|spec/i.test(f.path || f.name || ''))
    .slice(0, 12)
    .map((f) => _testFileForSource(f, fw));

  if (generated.length === 0) {
    generated.push(_testFileForSource({ path: 'app.py' }, fw));
  }
  return { framework: fw, files: generated };
};

// ----------------------------------------------------------------------------
// CI/CD manifests
// ----------------------------------------------------------------------------

export const synthesizeCicd = ({ architecture, requirementsReport, tests }) => {
  const projectName = _slug(requirementsReport?.title || requirementsReport?.idea_title || 'app');
  const archId = architecture?.id || 'serverless';
  const testCommand = tests?.framework === 'jest' || tests?.framework === 'jest-ts'
    ? 'npm test'
    : tests?.framework === 'go-test'
      ? 'go test ./...'
      : 'pytest';

  return {
    provider: archId === 'containerized' ? 'gitlab-ecr-ecs' : 'gitlab-aws-serverless',
    files: [
      {
        path: '.gitlab-ci.yml',
        content:
          `stages: [validate, test, build, deploy]\n\n` +
          `variables:\n` +
          `  AWS_DEFAULT_REGION: us-east-2\n` +
          `  PROJECT_NAME: ${projectName}\n\n` +
          `validate:\n` +
          `  stage: validate\n` +
          `  image: python:3.13\n` +
          `  script:\n` +
          `    - python --version\n` +
          `    - test -f README.md\n\n` +
          `test:\n` +
          `  stage: test\n` +
          `  image: python:3.13\n` +
          `  script:\n` +
          `    - pip install -r requirements.txt || true\n` +
          `    - ${testCommand}\n\n` +
          `build:\n` +
          `  stage: build\n` +
          `  image: docker:25\n` +
          `  services: [docker:25-dind]\n` +
          `  script:\n` +
          `    - docker build -t $PROJECT_NAME:$CI_COMMIT_SHORT_SHA .\n\n` +
          `deploy-dev:\n` +
          `  stage: deploy\n` +
          `  when: manual\n` +
          `  script:\n` +
          `    - ./scripts/deploy.sh dev\n`,
      },
      {
        path: 'scripts/deploy.sh',
        content:
          `#!/usr/bin/env bash\n` +
          `set -euo pipefail\n\n` +
          `STAGE="\${1:-dev}"\n` +
          `echo "Deploying ${projectName} to $STAGE"\n` +
          (archId === 'containerized'
            ? `cd infra && terraform init && terraform apply -auto-approve -var="stage=$STAGE"\n`
            : `serverless deploy --stage "$STAGE"\n`),
      },
    ],
  };
};

// ----------------------------------------------------------------------------
// Deployment manifests
// ----------------------------------------------------------------------------

export const synthesizeDeployment = ({ architecture, requirementsReport, cicd }) => {
  const projectName = _slug(requirementsReport?.title || requirementsReport?.idea_title || 'app');
  const archId = architecture?.id || 'serverless';

  if (archId === 'serverless') {
    return {
      provider: 'aws-serverless',
      files: [
        {
          path: 'serverless.yml',
          content:
            `service: ${projectName}\n\n` +
            `provider:\n` +
            `  name: aws\n` +
            `  runtime: python3.13\n` +
            `  region: us-east-2\n` +
            `  stage: \${opt:stage, 'dev'}\n` +
            `  environment:\n` +
            `    DDB_TABLE_NAME: \${self:service}-\${self:provider.stage}\n\n` +
            `functions:\n` +
            `  api:\n` +
            `    handler: handler.lambda_handler\n` +
            `    events:\n` +
            `      - http: { path: /, method: any }\n` +
            `      - http: { path: /{proxy+}, method: any }\n\n` +
            `resources:\n` +
            `  Resources:\n` +
            `    AppTable:\n` +
            `      Type: AWS::DynamoDB::Table\n` +
            `      Properties:\n` +
            `        TableName: \${self:provider.environment.DDB_TABLE_NAME}\n` +
            `        BillingMode: PAY_PER_REQUEST\n` +
            `        AttributeDefinitions:\n` +
            `          - { AttributeName: pk, AttributeType: S }\n` +
            `          - { AttributeName: sk, AttributeType: S }\n` +
            `        KeySchema:\n` +
            `          - { AttributeName: pk, KeyType: HASH }\n` +
            `          - { AttributeName: sk, KeyType: RANGE }\n`,
        },
        {
          path: 'DEPLOYMENT.md',
          content:
            `# AWS Deployment\n\n` +
            `Provider: AWS Serverless\n\n` +
            `## Human gate\n\n` +
            `Deployment should run only after Requirements, Design, Docs, Code, Tests, and CI/CD are approved.\n\n` +
            `## Command\n\n` +
            `\`\`\`bash\n${cicd?.files?.find((f) => f.path === 'scripts/deploy.sh') ? './scripts/deploy.sh dev' : 'serverless deploy --stage dev'}\n\`\`\`\n`,
        },
      ],
    };
  }

  if (archId === 'containerized') {
    return {
      provider: 'aws-ecs-fargate',
      files: [
        {
          path: 'Dockerfile',
          content:
            `FROM python:3.13-slim\n\n` +
            `WORKDIR /app\n` +
            `COPY requirements.txt .\n` +
            `RUN pip install --no-cache-dir -r requirements.txt\n` +
            `COPY . .\n` +
            `EXPOSE 8080\n` +
            `CMD ["uvicorn", "app:app", "--host", "0.0.0.0", "--port", "8080"]\n`,
        },
        {
          path: 'infra/main.tf',
          content:
            `terraform {\n  required_providers { aws = { source = "hashicorp/aws", version = "~> 5.0" } }\n}\n\n` +
            `provider "aws" { region = "us-east-2" }\n\n` +
            `resource "aws_ecs_cluster" "main" { name = "${projectName}" }\n\n` +
            `# TODO: ALB, target group, task definition, service, RDS Aurora cluster.\n`,
        },
        {
          path: 'DEPLOYMENT.md',
          content:
            `# AWS Deployment\n\n` +
            `Provider: ECS Fargate\n\n` +
            `Run the manual deploy job from CI/CD, or execute:\n\n` +
            `\`\`\`bash\n./scripts/deploy.sh dev\n\`\`\`\n`,
        },
      ],
    };
  }

  // modular-monolith default
  return {
    provider: 'docker-compose',
    files: [
      {
        path: 'Dockerfile',
        content:
          `FROM python:3.13-slim\nWORKDIR /app\nCOPY . .\nRUN pip install -r requirements.txt\nCMD ["python", "app.py"]\n`,
      },
      {
        path: 'docker-compose.yml',
        content:
          `services:\n  app:\n    build: .\n    ports: ["8000:8000"]\n    environment:\n      DATABASE_URL: postgres://app:app@db:5432/app\n    depends_on: [db]\n  db:\n    image: postgres:16\n    environment:\n      POSTGRES_USER: app\n      POSTGRES_PASSWORD: app\n      POSTGRES_DB: app\n    volumes: ["pgdata:/var/lib/postgresql/data"]\nvolumes: { pgdata: {} }\n`,
      },
      {
        path: 'DEPLOYMENT.md',
        content:
          `# Deployment\n\n` +
          `Build and deploy ${projectName} after the CI/CD stage is approved.\n\n` +
          `\`\`\`bash\ndocker compose up --build\n\`\`\`\n`,
      },
    ],
  };
};

// ----------------------------------------------------------------------------
// Documentation
// ----------------------------------------------------------------------------

export const synthesizeDocs = (project) => {
  const title =
    project?.idea?.title ||
    project?.requirements?.snapshot?.title ||
    project?.requirements?.snapshot?.idea_title ||
    'Untitled Project';
  const summary =
    project?.idea?.summary ||
    project?.requirements?.snapshot?.summary ||
    '';
  const arch =
    project?.architecture?.options?.find((o) => o.id === project?.architecture?.selectedId) || null;
  const codeFiles = project?.code?.snapshot?.files || [];
  const testFiles = project?.tests?.files || [];
  const cicdFiles = project?.cicd?.files || [];
  const deployFiles = project?.deployment?.files || [];

  const readme =
    `# ${title}\n\n` +
    (summary ? `${summary}\n\n` : '') +
    `## Architecture\n\n` +
    (arch
      ? `**${arch.name}** — ${arch.summary}\n\n` +
        `Components:\n${arch.components.map((c) => `- ${c}`).join('\n')}\n\n`
      : '_No architecture selected yet._\n\n') +
    `## Source layout\n\n` +
    (codeFiles.length
      ? codeFiles.slice(0, 25).map((f) => `- \`${f.path || f.name}\``).join('\n') + '\n\n'
      : '_No code generated yet._\n\n') +
    `## Tests\n\n` +
    (testFiles.length
      ? `Framework: \`${project?.tests?.framework || 'pytest'}\`\n\n` +
        testFiles.slice(0, 25).map((f) => `- \`${f.path}\``).join('\n') + '\n\n'
      : '_No tests generated yet._\n\n') +
    `## CI/CD\n\n` +
    (cicdFiles.length
      ? `Provider: \`${project?.cicd?.provider}\`\n\n` +
        cicdFiles.map((f) => `- \`${f.path}\``).join('\n') + '\n\n'
      : '_No CI/CD files generated yet._\n\n') +
    `## Deployment\n\n` +
    (deployFiles.length
      ? `Provider: \`${project?.deployment?.provider}\`\n\n` +
        deployFiles.map((f) => `- \`${f.path}\``).join('\n') + '\n\n'
      : '_No deployment manifests generated yet._\n\n') +
    `## Getting started\n\n` +
    `\`\`\`bash\n` +
    `# install dependencies\npip install -r requirements.txt\n\n` +
    `# run tests\npytest\n\n` +
    `# deploy (see \`serverless.yml\` / \`Dockerfile\`)\n` +
    `\`\`\`\n`;

  const adrs = arch
    ? [
        {
          path: `docs/adr/0001-architecture.md`,
          content:
            `# ADR 0001: Adopt ${arch.name}\n\n` +
            `## Status\n\nAccepted\n\n` +
            `## Context\n\n${summary || 'See requirements report.'}\n\n` +
            `## Decision\n\nWe adopt **${arch.name}**: ${arch.summary}\n\n` +
            `## Consequences\n\n` +
            `### Positive\n${arch.pros.map((p) => `- ${p}`).join('\n')}\n\n` +
            `### Negative\n${arch.cons.map((c) => `- ${c}`).join('\n')}\n`,
        },
      ]
    : [];

  return {
    readme,
    files: [{ path: 'README.md', content: readme }, ...adrs],
  };
};
